// The shared-screen video quad (M57.4, ARCH §30.6, COW-9, D-TF-180). The exact
// `VideoTexture`-on-a-plane construction `client/earth/overlay.ts` uses for its
// OVL-2 ghost plane: the mesher never sees this, the sim never learns a share
// exists, and a replay shows a dark screen — a plain THREE object with no sim
// counterpart at all, added/removed straight from a THREE.Scene/Group.
import * as THREE from 'three';
import { COW } from '../../sim/config';

export class ScreenQuad {
  private mesh: THREE.Mesh | null = null;
  private mat: THREE.MeshBasicMaterial | null = null;
  private tex: THREE.VideoTexture | null = null;
  private videoEl: HTMLVideoElement | null = null;

  constructor(private readonly group: THREE.Scene) {}

  /** True once a real video frame is backing the quad (test/debug seam — "a
   *  non-null video texture", M57.4's own Verify wording). */
  get hasTexture(): boolean {
    return this.tex !== null;
  }

  /** Build (or replace) the quad at (x,y,z), on the face named by `normal`
   *  (a unit axis vector — which side of the block it faces), offset
   *  `COW.SCREEN_OFFSET` in front of it. One block wide/tall — §30.6's
   *  multi-block quad-joining is deferred (D-TF-284): a spike behind a human
   *  gate doesn't need the general case to prove the mechanism works. */
  show(x: number, y: number, z: number, normal: { x: number; y: number; z: number }, stream: MediaStream): void {
    this.dispose();
    const video = document.createElement('video');
    video.muted = true;
    video.autoplay = true;
    video.playsInline = true;
    video.srcObject = stream;
    void video.play().catch(() => {});
    this.videoEl = video;
    const tex = new THREE.VideoTexture(video);
    this.tex = tex;
    const mat = new THREE.MeshBasicMaterial({ map: tex, side: THREE.DoubleSide });
    this.mat = mat;
    const geom = new THREE.PlaneGeometry(1, 1);
    const mesh = new THREE.Mesh(geom, mat);
    mesh.frustumCulled = false;
    const n = new THREE.Vector3(normal.x, normal.y, normal.z).normalize();
    mesh.position.set(
      x + 0.5 + n.x * (0.5 + COW.SCREEN_OFFSET),
      y + 0.5 + n.y * (0.5 + COW.SCREEN_OFFSET),
      z + 0.5 + n.z * (0.5 + COW.SCREEN_OFFSET),
    );
    mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), n);
    this.mesh = mesh;
    this.group.add(mesh);
  }

  /** Darken (remove) the quad — Stop, a disconnect, breaking the block, or the
   *  peer connection ending (E-62/E-64/E-65). */
  dispose(): void {
    if (this.mesh) { this.group.remove(this.mesh); this.mesh.geometry.dispose(); }
    this.mat?.dispose();
    this.tex?.dispose();
    if (this.videoEl) { this.videoEl.srcObject = null; this.videoEl.remove(); }
    this.mesh = null; this.mat = null; this.tex = null; this.videoEl = null;
  }
}
